import { useTranslation } from 'react-i18next';
import { colors } from '@/theme/colors';
import type { IconVariant } from '@/ui/icons/icon';
import { useUserAccount } from '@/user/use-user-account';

import { getSpotlightAccountDisplayName } from '../get-spotlight-account-display-name';
import { useSpotlightCompaniesKpi } from '../companies-listing/use-spotlight-companies.query';
import { useSpotlightDefaultFilters } from '../companies-listing/use-spotlight-default-filters';

type Summary = Record<
  string,
  {
    description: string;
    icon: { background: string; color: string; variant: IconVariant };
    tooltip: { description: string; title: string };
    value: number;
  }
>;

export const useSpotlightSummary = () => {
  const { t } = useTranslation('spotlight');
  const account = useUserAccount();
  const accountName = getSpotlightAccountDisplayName(account);
  const {
    filters: defaultSpotlightFilters,
    isReady: isDefaultSpotlightFiltersReady,
  } = useSpotlightDefaultFilters();
  const { data: companiesKpi, isLoading: isCompaniesKpiLoading } =
    useSpotlightCompaniesKpi(
      '',
      defaultSpotlightFilters,
      isDefaultSpotlightFiltersReady,
    );

  if (
    !isDefaultSpotlightFiltersReady ||
    isCompaniesKpiLoading ||
    !companiesKpi
  ) {
    return [];
  }

  const summary: Summary = {
    organizations: {
      description: t`validatedCompanies`,
      icon: {
        background: colors.spotlight.icon.blue.bg,
        color: colors.spotlight.icon.blue.color,
        variant: 'SpotlightValidatedCompanies',
      },
      tooltip: {
        description: t('tooltips.validatedCompanies.description', {
          accountName,
        }),
        title: t('tooltips.validatedCompanies.title'),
      },
      value: companiesKpi.companies,
    },
    projects: {
      description: t`projects`,
      icon: {
        background: colors.spotlight.icon.green.bg,
        color: colors.spotlight.icon.green.color,
        variant: 'SpotlightProjects',
      },
      tooltip: {
        description: t('tooltips.projects.description', { accountName }),
        title: t('tooltips.projects.title'),
      },
      value: companiesKpi.projects,
    },
    pilots: {
      description: t`pilots`,
      icon: {
        background: colors.spotlight.icon.amber.bg,
        color: colors.spotlight.icon.amber.color,
        variant: 'SpotlightPilots',
      },
      tooltip: {
        description: t('tooltips.pilots.description'),
        title: t('tooltips.pilots.title'),
      },
      value: companiesKpi.pilots,
    },
    otherDeals: {
      description: t`otherDeals`,
      icon: {
        background: colors.spotlight.icon.purple.bg,
        color: colors.spotlight.icon.purple.color,
        variant: 'SpotlightOtherDeals',
      },
      tooltip: {
        description: t('tooltips.otherDeals.description'),
        title: t('tooltips.otherDeals.title'),
      },
      value: companiesKpi.otherDeals,
    },
  };

  return summary;
};
